import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { TagInput } from '@/components/ui/tag-input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import {
  documentTagsSchema,
  type DocumentTagsFormValues,
} from '@/features/documents/schemas/documentTags.schema';

interface DocumentTagsDialogProps {
  open: boolean;
  initialTags: string[];
  onOpenChange: (open: boolean) => void;
  onSave: (tags: string[]) => void;
}

const DocumentTagsDialog: React.FC<DocumentTagsDialogProps> = ({
  open,
  initialTags,
  onOpenChange,
  onSave,
}) => {
  const { t } = useTranslation();

  const form = useForm<DocumentTagsFormValues>({
    resolver: zodResolver(documentTagsSchema),
    defaultValues: { tags: initialTags },
  });

  // DocumentListView keeps this dialog MOUNTED and passes `tagTarget?.tags ?? []`,
  // so `initialTags` is a fresh array on every parent render. Re-seeding on that
  // identity throws away whatever the user has typed the moment anything above
  // re-renders — key on the tag CONTENT instead, the same fix useVersionHistory
  // uses for its analysisHistory prop.
  // U+0000 as the separator, written as an escape: a raw NUL byte in the source
  // makes git classify this file as BINARY (no diff, no line review, no merge).
  // NUL cannot occur in a tag, so ['a b'] and ['a','b'] cannot collide on one key.
  const seedKey = initialTags.join('\u0000');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const seed = useMemo(() => initialTags, [seedKey]);

  useEffect(() => {
    // reset(), not setValue(): it re-baselines the form so a freshly opened
    // dialog is clean, and it clears any validation error left from last time.
    if (open) form.reset({ tags: seed });
  }, [open, seed, form]);

  // No dirty guard here on purpose. Closing this dialog has always discarded
  // unsaved tags silently; adding a confirmation would be a behaviour change,
  // and `form.formState.isDirty` is deliberately not read (a Proxy flag read
  // for the first time inside a callback never updates).
  const submit = (values: DocumentTagsFormValues) => onSave(values.tags);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="revy-ui sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('chat.editTags')}</DialogTitle>
          <DialogDescription>{t('chat.tagInputPlaceholder')}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          {/*
            min-w-0: as a grid child of DialogContent the form defaults to
            min-width:auto, so an unbreakable long tag pushed it to 804px inside
            a 448px dialog and every child inherited that width. Allowing it to
            shrink is what makes the chip's truncation resolve against the real
            dialog width.
          */}
          <form className="min-w-0" onSubmit={(event) => void form.handleSubmit(submit)(event)}>
            {/*
              No FormLabel: tags are optional here (the route documents
              「空配列で全削除」), so there is no required asterisk to render, and
              TagInput already names its field with `addLabel`.
            */}
            <FormField
              control={form.control}
              name="tags"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    {/* commitOnBlur preserves this dialog's original behaviour:
                        leaving the field kept what you had typed. */}
                    <TagInput
                      value={field.value}
                      onChange={field.onChange}
                      commitOnBlur
                      placeholder={t('chat.tagInputPlaceholder')}
                      addLabel={t('chat.tagInputPlaceholder')}
                      removeLabel={t('common.delete')}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('common.cancel')}
              </Button>
              {/*
                Never disabled, exactly as before: an empty tag list is a valid
                save (it clears every tag). Gating on formState.isValid would
                need mode:'onChange' and would surface errors while typing.
              */}
              <Button type="submit">{t('common.save')}</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentTagsDialog;
